import { createRouter, RouterProvider, createRootRoute, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import PublicLogin from './pages/PublicLogin';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import LiveInspection from './pages/LiveInspection';
import Reports from './pages/Reports';
import Analytics from './pages/Analytics';
import SystemStatus from './pages/SystemStatus';
import ProfileSetup from './components/ProfileSetup';
import { useGetCallerUserProfile } from './hooks/useQueries';

function AppShell() {
  const { user, isInitializing, isAuthenticated } = useAuth();
  const { data: userProfile, isLoading: profileLoading, isFetched } = useGetCallerUserProfile();

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm font-mono">INITIALIZING SYSTEM...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PublicLogin />;
  }

  const showProfileSetup = isAuthenticated && !profileLoading && isFetched && userProfile === null;

  if (showProfileSetup) {
    return <ProfileSetup />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const rootRoute = createRootRoute({
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: Dashboard,
});

const liveInspectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/live-inspection',
  component: LiveInspection,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: Reports,
});

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: Analytics,
});

const systemStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/system-status',
  component: SystemStatus,
});

// Secure Access Portal — kept for internal/role-based access
const secureAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/secure-access',
  component: LoginPage,
});

// Public login page — also accessible via /login
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: PublicLogin,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  liveInspectionRoute,
  reportsRoute,
  analyticsRoute,
  systemStatusRoute,
  secureAccessRoute,
  loginRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  return <RouterProvider router={router} />;
}
